/**
 * @copyright Copyright 2024-2025 Epic Systems Corporation
 * @file banner of a showroom stage
 * @module Epic.AppOrchard.Showroom.StageBanner
 */

import { Box, BoxProps, Flex, Heading, Image, VStack } from "@chakra-ui/react";
import { showroomText } from "ao/chakraTheme";
import { PopoverWithHover } from "ao/components/Core/PopoverWithHover";
import { IGalleryStageModel } from "ao/types/showroom";
import { getBaseUrl } from "ao/utils/helpers";
import { FunctionComponent, memo, useMemo } from "react";

interface IProps {
	stage: IGalleryStageModel;
	showComingSoon?: boolean;
}

type Props = IProps & BoxProps;

export const StageBanner: FunctionComponent<Props> = memo((props) => {
	const { stage, showComingSoon, ...rest } = props;

	const baseUrl = useMemo(getBaseUrl, []);

	const bannerContents = useMemo(
		() => (
			<Flex alignItems="center" tabIndex={0} cursor="pointer">
				{stage.iconPath && (
					<Image
						src={`${baseUrl}Content/images/${stage.iconPath}`}
						fallback={<></>}
						maxH="25px"
						maxW="25px"
					/>
				)}
				<VStack alignItems="center" ml="5px">
					<Heading
						as="h4"
						fontWeight="600"
						fontSize="1.1rem"
						fontFamily="Jost"
						color={showroomText}
						whiteSpace={"nowrap"}
					>
						{stage.groupName.toLocaleUpperCase()}
					</Heading>
					{showComingSoon && (
						<Heading
							as="h5"
							fontWeight="500"
							fontSize="0.75rem"
							fontFamily="Jost"
							color={showroomText}
							whiteSpace={"nowrap"}
							margin={"0px !important"}
							borderTop={`2px dashed ${showroomText}`}
						>
							{stage.comingSoonTitle}
						</Heading>
					)}
				</VStack>
			</Flex>
		),
		[stage.iconPath, stage.groupName, stage.comingSoonTitle, baseUrl, showComingSoon],
	);

	return (
		<Flex
			width="100%"
			justify="center"
			background={`linear-gradient(42deg, ${stage.backgroundColor2 ?? "#D6FFDC"} 0%, ${
				stage.backgroundColor ?? "#A6EBAF"
			} 100%)`}
			py="7px"
			{...rest}
		>
			{stage.description ? (
				<PopoverWithHover
					popoverContent={
						<Box
							dangerouslySetInnerHTML={{
								__html: stage.description,
							}}
						/>
					}
					popoverStyles={{ bgColor: "white" }}
				>
					{bannerContents}
				</PopoverWithHover>
			) : (
				<>{bannerContents}</>
			)}
		</Flex>
	);
});
